import type { ComputedRef, Ref } from 'vue'
import type { NotificationInboxItem } from '~/services/api'
import { useLogger } from '~/composables/useLogger'
import { useIdentity } from '~/composables/useIdentity'
import { Normalization } from '~/utils/normalization'

type MaybeBooleanRef = Ref<boolean> | ComputedRef<boolean>

interface NavigationNotificationsOptions {
  enabled?: MaybeBooleanRef
}

export const useNotificationsNav = (options: NavigationNotificationsOptions = {}) => {
  const logger = useLogger('useNotificationsNav')
  const { isAuthenticated, identity } = useIdentity()
  const userStore = useUserStore()
  const notificationsStore = useNotificationsStore()
  const toast = useToast()
  const {
    loadInitial: loadInitialNotifications,
    loadMore: loadMoreNotifications,
    subscribe: subscribeToNotifications,
    markAllAsRead,
    deleteNotifications,
    openNotification,
    reset: resetNotifications
  } = useNotifications()

  const isDeletingNotifications = ref(false)
  const notificationDeleteSuccessKey = ref(0)

  const isEnabled = computed(() => options.enabled?.value ?? true)
  const showNotifications = computed(() => isAuthenticated.value && isEnabled.value)

  const notificationUserId = computed(() => {
    const currentUser = identity.value ?? userStore.user
    return Normalization.stringValue(currentUser?.id, null)
  })

  const bootstrapNotifications = async () => {
    if (
      import.meta.server ||
      !showNotifications.value ||
      notificationUserId.value === null ||
      notificationUserId.value.length === 0
    ) {
      return
    }

    try {
      await loadInitialNotifications()
      await subscribeToNotifications()
    } catch (error) {
      logger.error('bootstrapNotifications', error)
    }
  }

  const handleMarkAllNotificationsAsRead = async () => {
    await markAllAsRead()
  }

  const handleNotificationSelect = async (notification: NotificationInboxItem) => {
    try {
      await openNotification(notification)
    } catch (error) {
      logger.error('handleNotificationSelect', error)
    }
  }

  const handleLoadMoreNotifications = async () => {
    try {
      await loadMoreNotifications()
    } catch (error) {
      logger.error('handleLoadMoreNotifications', error)
    }
  }

  const handleDeleteNotifications = async (notificationIds: string[]) => {
    if (!notificationIds.length || isDeletingNotifications.value) {
      return
    }

    isDeletingNotifications.value = true

    try {
      const response = await deleteNotifications(notificationIds)
      const deletedCount = response?.data.deleted_count ?? 0

      if (deletedCount > 0) {
        notificationDeleteSuccessKey.value += 1
        toast.success(
          deletedCount === 1
            ? 'Notification deleted successfully.'
            : 'Notifications deleted successfully.'
        )
      }
    } catch (error) {
      toast.error('Unable to clear notifications right now.')
      logger.error('handleDeleteNotifications', error)
    } finally {
      isDeletingNotifications.value = false
    }
  }

  watch(
    () => [showNotifications.value, notificationUserId.value] as const,
    async ([canShowNotifications, nextUserId], previous = [false, null] as const) => {
      const [previousCanShowNotifications, previousUserId] = previous

      if (import.meta.server) {
        return
      }

      if (canShowNotifications && nextUserId !== null && nextUserId.length > 0) {
        await bootstrapNotifications()
        return
      }

      const hadPreviousNotifications =
        previousCanShowNotifications || (previousUserId !== null && previousUserId.length > 0)
      if (!isAuthenticated.value && hadPreviousNotifications) {
        await resetNotifications()
      }
    },
    { immediate: true }
  )

  return {
    notificationsStore,
    showNotifications,
    isDeletingNotifications,
    notificationDeleteSuccessKey,
    handleMarkAllNotificationsAsRead,
    handleNotificationSelect,
    handleLoadMoreNotifications,
    handleDeleteNotifications,
    resetNotifications
  }
}
